import { useEffect, useState } from "react";

import { CloseOutlined, SaveOutlined } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";

export const InvoiceItemDialog = ({ open, onClose, onSave, initialValues = null, addOnTypes = [] }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useState({
    subscription_type: "",
    quantity: 1,
    price_override: "",
    amount_minimum: 0,
    subtotal: 0,
    description: "",
  });

  useEffect(() => {
    if (initialValues) {
      setState({ ...initialValues, quantity: initialValues.quantity || 1 });
    } else {
      setState({
        subscription_type: "",
        quantity: 1,
        price_override: "",
        amount_minimum: 0,
        subtotal: 0,
        description: "",
      });
    }
  }, [initialValues, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setState((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "subscription_type") {
      const selected = addOnTypes.find((t) => t.subscription_type === value);
      if (selected) {
        setState((prev) => ({
          ...prev,
          subtotal: selected.subtotal,
          amount_minimum: selected.amount_minimum ?? 0,
          description: selected.name,
        }));
      }
    }
  };

  const handleSave = () => {
    if (!state.subscription_type || !state.quantity || parseFloat(state.quantity) <= 0) {
      enqueueSnackbar("Please select an add-on type.", {
        preventDuplicate: true,
        variant: "error",
      });
      return;
    }

    try {
      onSave({
        ...state,
        quantity: parseFloat(state.quantity),
        price_override: state.price_override ? parseFloat(state.price_override) : null,
      });

      onClose();
    } catch (err) {
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
        preventDuplicate: true,
        variant: "error",
      });
    }
  };

  const computedLineTotal = Math.max(
    (parseFloat(state.price_override) || state.subtotal) * (parseFloat(state.quantity) || 0),
    state.amount_minimum ?? 0
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initialValues ? "Edit Add-On" : "Add Add-On"}</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="addon-type-label">Add-On Type</InputLabel>
              <Select
                labelId="addon-type-label"
                value={state.subscription_type}
                name="subscription_type"
                onChange={handleChange}
              >
                {addOnTypes.map((t) => (
                  <MenuItem key={t.subscription_type} value={t.subscription_type}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Quantity"
              name="quantity"
              type="number"
              fullWidth
              value={state.quantity}
              onChange={handleChange}
              inputProps={{ step: 0.01, min: 1 }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Price Override"
              name="price_override"
              type="number"
              fullWidth
              value={state.price_override}
              onChange={handleChange}
              inputProps={{ step: 0.01, min: 0 }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Minimum" value={state.amount_minimum.toFixed(2)} fullWidth disabled />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Line Total" value={computedLineTotal.toFixed(2)} fullWidth disabled />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="contained" color="secondary" onClick={onClose}>
            <CloseOutlined sx={{ mr: 1 }} />
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            <SaveOutlined sx={{ mr: 1 }} />
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
